import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/api/client";
import { toast } from "sonner";

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          data-testid={`review-star-${i}`}
          onMouseEnter={() => setHover(i)}
          onClick={() => onChange(i)}
          className="p-0.5"
          aria-label={`${i} star${i > 1 ? "s" : ""}`}
        >
          <Star className={`h-6 w-6 ${i <= active ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />
        </button>
      ))}
    </div>
  );
}

export default function ReviewButton({ orderId, item, canReview, onReviewed }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    // Cheap client check: look up my reviews to see if this product is already covered.
    api.get("/reviews/my").then((r) => {
      if (r.data && r.data[item.product_id]) setExisting(r.data[item.product_id]);
    }).catch(() => {});
  }, [item.product_id]);

  if (!canReview) return null;
  if (existing) {
    return (
      <div data-testid={`review-done-${item.product_id}`} className="mt-2 text-xs text-green-700 flex items-center gap-1.5">
        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
        You rated this {existing.rating}/5 — thanks!
      </div>
    );
  }

  const submit = async () => {
    if (body.trim().length < 3) return toast.error("Please write at least a few words.");
    setBusy(true);
    try {
      const { data } = await api.post(`/reviews/product/${item.product_id}`, { rating, body: body.trim(), order_id: orderId });
      setExisting(data);
      setOpen(false);
      toast.success("Thanks for the review!");
      onReviewed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save review");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`review-open-${item.product_id}`}
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#F48E2E] text-white hover:bg-[#D97815]"
      >
        <Star className="h-3.5 w-3.5" /> Write a review
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle>Review {item.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Your rating</Label>
              <StarPicker value={rating} onChange={setRating} />
            </div>
            <div>
              <Label>Your review</Label>
              <Textarea
                data-testid="review-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Fit, fabric, packaging, seller experience — anything future buyers should know."
                rows={4}
              />
              <p className="text-[11px] text-gray-500 mt-1">Reviews are marked as verified purchases and shown on the product and seller pages.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} className="rounded-full">Cancel</Button>
              <Button data-testid="review-submit" onClick={submit} disabled={busy} className="rounded-full bg-[#F48E2E] hover:bg-[#D97815]">
                {busy ? "Posting…" : "Post review"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
